import { type FormEvent, useId, useState } from 'react';
import { Icon } from '../../ui/Icon';
import s from './ScannerView.module.css';
import { useScanner } from './useScanner';

/**
 * The viewfinder plus the two ways of working when it cannot be used. The camera is never the
 * only route to a code: a Bluetooth scanner types into the page and the worker can type into
 * the field, so a blocked, missing or unsupported camera still leaves a working screen.
 */
export function ScannerView({ onCode, enabled = true }: { onCode: (raw: string) => void; enabled?: boolean }) {
  const { videoRef, cameraOn, cameraError, startCamera, stopCamera, submitManual } = useScanner({ onCode, enabled });
  const [typed, setTyped] = useState('');
  const fieldId = useId();

  // Switched off means off for all three ways in. A screen turns the scanner off while a refusal
  // holds it or a send, close or finish is in flight, and a typed code is as much a scan as a read.
  // What was typed stays in the field, to add once the screen takes codes again.
  const add = (e: FormEvent) => {
    e.preventDefault();
    const code = typed.trim();
    if (!code || !enabled) return;
    submitManual(code);
    setTyped('');
  };

  return (
    <div className={s.scanner}>
      {/* The video element stays mounted even while the frame is collapsed, so the ref the hook
          needs is always there and "Try again" has something to open the camera into. */}
      <div className="viewfinder camera" hidden={Boolean(cameraError)}>
        <video ref={videoRef} className={s.stream} playsInline muted autoPlay />
        {!cameraOn && <p className={s.veil}>Camera off</p>}
        <div className="reticle" />
        <div className="hint">Point at the sticker, or use a Bluetooth scanner</div>
      </div>

      {cameraError && (
        <div className={s.camNote} role="alert">
          <Icon name="alert" />
          <p className={s.camNoteText}>{cameraError}</p>
          <button type="button" className={s.camRetry} onClick={startCamera}>
            Try again
          </button>
        </div>
      )}

      {/* The icon stays put and the word carries the state, so the control never reads as the
          opposite of what it is. */}
      <button type="button" className={`bigbtn ghost ${s.camBtn}`} aria-pressed={cameraOn} onClick={cameraOn ? stopCamera : startCamera}>
        <Icon name="scan" />
        Camera
        <span className={s.camState}>{cameraOn ? 'On' : 'Off'}</span>
      </button>

      <form className={s.manual} onSubmit={add}>
        <label className={s.manualLabel} htmlFor={fieldId}>
          Box code
        </label>
        <div className={`inp lg ${s.manualField}`}>
          <Icon name="keyboard" />
          <input
            id={fieldId}
            className={s.manualInput}
            data-scan-input="true"
            type="text"
            placeholder="Type a box code"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            autoComplete="off"
            autoCapitalize="characters"
            autoCorrect="off"
            spellCheck={false}
            enterKeyHint="done"
          />
        </div>
        <button type="submit" className={`bigbtn ${s.manualAdd}`} disabled={!enabled || typed.trim().length === 0}>
          Add
        </button>
      </form>
    </div>
  );
}
